import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { UtilityService } from 'src/app/core/services/utility/utility.service';
import { HttpService } from 'src/app/services/http/http.service';
import { environment } from 'src/environments/environment';

declare const google: any;

@Component({
    selector: 'app-trip-breadcrumbs',
    templateUrl: './trip-breadcrumbs.component.html',
    styleUrls: ['./trip-breadcrumbs.component.scss'],
})
export class TripBreadcrumbsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() session_id: string;
    @Input() trip_leg_id?: number | string;
    @Input() driver_id?: number | string;

    pageNo: number = 1;
    itemsPerPage: number = 10;
    totalItems: number = 0;
    showPagination: boolean = false;
    isLoading: boolean = false;
    breadcrumbs: any[] = [];

    addressMap: Map<string, string> = new Map();
    loadingAddressMap: Map<string, boolean> = new Map();

    statusOptions = ['pickup_started', 'arrived_at_pickup', 'en_route', 'at_drop_off', 'completed', 'no_show'];

    selectedStatusFilter: string | null = null;

    private subscriptionList$: Subscription = new Subscription();

    constructor(private httpService: HttpService, private cookieService: CookieService, public utilityService: UtilityService) {
        this.itemsPerPage = this.utilityService.default_display_filter || 10;
    }

    ngOnInit(): void {
        if (this.session_id) {
            this.getBreadcrumbs();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['session_id'] && !changes['session_id'].firstChange && this.session_id) {
            this.pageNo = 1;
            this.selectedStatusFilter = null;
            this.getBreadcrumbs();
        }
    }

    selectStatusFilter(statusKey: string | null): void {
        this.selectedStatusFilter = statusKey;
        this.pageNo = 1;
        this.getBreadcrumbs();
    }

    getBreadcrumbs(pageNo: number = 1, itemsPerPage: number = this.itemsPerPage): void {
        if (!this.session_id) {
            return;
        }

        this.pageNo = pageNo;
        this.itemsPerPage = itemsPerPage;
        this.isLoading = true;
        this.breadcrumbs = [];
        // Clear address maps when loading new breadcrumbs
        this.addressMap.clear();
        this.loadingAddressMap.clear();

        const payload: any = {
            web_access_token: this.cookieService.get('web_access_token'),
            limit: this.itemsPerPage,
            offset: (pageNo - 1) * this.itemsPerPage,
            session_id: this.session_id,
            sort_order: 'DESC',
        };

        if (this.trip_leg_id) {
            payload.trip_leg_id = this.trip_leg_id;
        }

        if (this.driver_id) {
            payload.driver_id = this.driver_id;
        }

        if (this.selectedStatusFilter) {
            payload.status = this.selectedStatusFilter;
        }

        this.subscriptionList$.add(
            this.httpService.postJSON(environment.urlWC + 'get_ride_breadcrumbs', payload).subscribe(
                (data: any) => {
                    if (data) {
                        this.breadcrumbs = data.breadcrumbs || data.data || [];
                        this.totalItems = data.count || data.total || 0;
                        this.showPagination = this.totalItems > this.itemsPerPage;
                    }
                    this.isLoading = false;
                },
                (err) => {
                    this.isLoading = false;
                    this.utilityService.toast('error', err?.message || 'Failed to load breadcrumbs');
                }
            )
        );
    }

    /**
     * Get Address From Lat Lng using Google Maps Geocoder
     */
    getAddressFromLatLng(breadcrumb: any): void {
        if (!breadcrumb.latitude || !breadcrumb.longitude) {
            this.utilityService.toast('error', 'Latitude and Longitude are required');
            return;
        }

        const breadcrumbId = breadcrumb.id.toString();

        // If address already exists, don't fetch again
        if (this.addressMap.has(breadcrumbId)) {
            return;
        }

        // If already loading, don't fetch again
        if (this.loadingAddressMap.get(breadcrumbId)) {
            return;
        }

        // Set loading state
        this.loadingAddressMap.set(breadcrumbId, true);

        const latlng = new google.maps.LatLng(parseFloat(breadcrumb.latitude), parseFloat(breadcrumb.longitude));

        const geocoder = new google.maps.Geocoder();
        geocoder
            .geocode({ location: latlng }, (results: any, status: string) => {
                this.loadingAddressMap.set(breadcrumbId, false);
                if (status === 'OK') {
                    if (results[0]) {
                        this.addressMap.set(breadcrumbId, results[0].formatted_address);
                    } else {
                        this.addressMap.set(breadcrumbId, 'No address found');
                        this.utilityService.toast('error', 'No address found for this location');
                    }
                } else {
                    this.addressMap.set(breadcrumbId, 'Unable to fetch address');
                    this.utilityService.toast('error', `Geocoder failed: ${status}`);
                }
            })
            .finally(() => {
                console.log('deleting breadcrumbId', breadcrumbId);
                this.loadingAddressMap.delete(breadcrumbId);
            });
    }

    ngOnDestroy(): void {
        this.subscriptionList$.unsubscribe();
    }
}
